/**
 * tiptap-figure.js — 워드형 에디터용 TipTap 확장.
 *  - Figure: 캡션(figcaption) + 자유 width + float + 드래그 리사이즈 NodeView
 * 글자 크기/색은 TipTap v3 공식 확장(TextStyle + FontSize + Color, extension-text-style)을
 * MimioRichEditor 에서 직접 등록한다 — 셋이 같은 textStyle 마크를 공유하도록 설계돼
 * bold·크기·색이 함께 적용됨.
 * POC(tiptap-editor-poc-v2.html)에서 검증된 로직 이식.
 */
import { Node } from '@tiptap/core';

// 캡션 + width + float + 드래그 핸들 이미지 figure
export const Figure = Node.create({
  name: 'figure',
  group: 'block',
  content: 'inline*',
  draggable: true,
  isolating: true,

  addAttributes() {
    return {
      kind: { default: 'image' }, // image | youtube (동영상도 이미지처럼 크기/배치/캡션)
      src: { default: null },
      alt: { default: '' },
      width: { default: '46%' }, // 자유 폭 (프리셋/드래그 공용)
      float: { default: 'none' }, // left | right | none
    };
  },

  parseHTML() {
    return [
      {
        // 일반 <figure> 도 인식(외부 에디터/웹페이지 붙여넣기 호환). img 없으면 제외(getAttrs false).
        tag: 'figure',
        // figcaption 이 없는 figure(마이그레이션 데이터 등)도 안 깨지게 — 없으면 빈 figcaption 생성
        contentElement: (dom) => {
          let cap = dom.querySelector('figcaption');
          if (!cap) {
            cap = dom.ownerDocument.createElement('figcaption');
            dom.appendChild(cap);
          }
          return cap;
        },
        getAttrs: (el) => {
          const img = el.querySelector('img');
          const iframe = el.querySelector('iframe');
          if (!img && !iframe) return false; // 이미지/동영상 없는 figure 는 이 노드로 안 잡음
          if (iframe) {
            return {
              kind: 'youtube',
              src: iframe.getAttribute('src') || null,
              width: el.style.width || '70%',
              float: el.getAttribute('data-float') || 'none',
            };
          }
          return {
            kind: 'image',
            src: img.getAttribute('src') || null,
            alt: img.getAttribute('alt') || '',
            width: el.style.width || '46%',
            float: el.getAttribute('data-float') || 'none',
          };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const { src, alt, width, float, kind } = node.attrs;
    const figAttrs = {
      'data-type': 'mimio-figure',
      'data-float': float,
      class: `fig fig-${float}${kind === 'youtube' ? ' fig-video' : ''}`,
      style: `width:${width}`,
    };
    if (kind === 'youtube') {
      return [
        'figure',
        figAttrs,
        [
          'div',
          { class: 'video-wrap' },
          [
            'iframe',
            {
              src,
              frameborder: '0',
              allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
              allowfullscreen: 'true',
            },
          ],
        ],
        ['figcaption', 0],
      ];
    }
    return ['figure', figAttrs, ['img', { src, alt }], ['figcaption', 0]];
  },

  addCommands() {
    return {
      insertFigure: (attrs) => ({ chain }) =>
        chain()
          .insertContent({ type: 'figure', attrs, content: [{ type: 'text', text: '캡션 (선택)' }] })
          .run(),
      // 동영상(YouTube) figure — 이미지와 동일한 크기/배치/캡션 컨트롤 재사용
      insertVideoFigure: (attrs) => ({ chain }) =>
        chain()
          .insertContent({
            type: 'figure',
            attrs: { ...attrs, kind: 'youtube' },
            content: [{ type: 'text', text: '캡션 (선택)' }],
          })
          .run(),
    };
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const isVideo = (node.attrs.kind || 'image') === 'youtube';
      const dom = document.createElement('figure');
      dom.setAttribute('data-type', 'mimio-figure');
      const wrap = document.createElement('div');
      wrap.className = isVideo ? 'video-wrap' : 'img-wrap';
      let media;
      if (isVideo) {
        media = document.createElement('iframe');
        media.setAttribute('frameborder', '0');
        media.setAttribute('allowfullscreen', 'true');
        media.setAttribute(
          'allow',
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
        );
      } else {
        media = document.createElement('img');
      }
      const cap = document.createElement('figcaption');
      const handle = document.createElement('span');
      handle.className = 'resize-handle';
      wrap.appendChild(media);
      wrap.appendChild(handle);
      dom.appendChild(wrap);
      dom.appendChild(cap);

      const paint = (n) => {
        dom.classList.add('fig');
        dom.classList.remove('fig-left', 'fig-right', 'fig-none');
        dom.classList.add(`fig-${n.attrs.float}`);
        if (isVideo) dom.classList.add('fig-video');
        dom.style.width = n.attrs.width;
        dom.setAttribute('data-float', n.attrs.float);
        media.src = n.attrs.src || '';
        if (!isVideo) media.alt = n.attrs.alt || '';
      };
      paint(node);

      // 드래그 크기조절
      handle.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const parentW = dom.parentElement.offsetWidth;
        const startX = e.clientX;
        const startW = dom.offsetWidth;
        const onMove = (ev) => {
          let w = startW + (ev.clientX - startX);
          w = Math.max(80, Math.min(parentW, w));
          dom.style.width = `${Math.round((w / parentW) * 100)}%`;
        };
        const onUp = () => {
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
          if (typeof getPos === 'function') {
            const p = getPos();
            editor
              .chain()
              .focus()
              .command(({ tr }) => {
                tr.setNodeAttribute(p, 'width', dom.style.width);
                return true;
              })
              .setNodeSelection(p)
              .run();
          }
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      });

      return {
        dom,
        contentDOM: cap,
        selectNode() {
          dom.classList.add('ProseMirror-selectednode');
        },
        deselectNode() {
          dom.classList.remove('ProseMirror-selectednode');
        },
        update(updated) {
          if (updated.type.name !== 'figure') return false;
          // kind(이미지↔동영상)가 바뀌면 미디어 엘리먼트 종류가 달라 NodeView 재생성 필요
          if (((updated.attrs.kind || 'image') === 'youtube') !== isVideo) return false;
          paint(updated);
          return true;
        },
        ignoreMutation(m) {
          if (m.type === 'selection') return false;
          return !(cap === m.target || cap.contains(m.target));
        },
      };
    };
  },
});
